import type { FC, PropsWithChildren } from 'react';
import { Box, Container, Stack } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Link } from 'react-router-dom';
import { Logo } from 'src/components/logos/logo-kh';
import { SentryButton } from 'src/components/sentry-button';
import { useTenantContext } from 'src/hooks/use-context';
import { paths } from 'src/paths';
import { TOP_NAV_HEIGHT } from './config';

const Root = styled('div')(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'top center',
  backgroundImage: 'url("/assets/gradient-bg.svg")',
  display: 'flex',
  flex: '1 1 auto',
  flexDirection: 'column',
  height: '100%'
}));

const Header = styled('header')(({ theme }) => ({
  left: 0,
  position: 'fixed',
  right: 0,
  top: 0,
  zIndex: theme.zIndex.appBar
}));

const Body = styled('div')({
  alignItems: 'center',
  display: 'flex',
  justifyContent: 'center',
  flex: '1 1 auto'
});

const AppName = styled('div')(({ theme }) => ({
  color: theme.palette.text.primary,
  fontFamily: "'Plus Jakarta Sans', sans-serif",
  fontSize: 20,
  fontWeight: 800,
  letterSpacing: '0.3px',
  lineHeight: 2.5
}));

/**
 * Simple landing page layout
 */
export const Layout: FC<PropsWithChildren> = (props) => {
  const { children } = props;

  // application name is displayed in the header
  const { style } = useTenantContext();

  return (
    <Root>
      <Header>
        <Container maxWidth="lg">
          <Stack
            direction="row"
            spacing={2}
            sx={{ height: TOP_NAV_HEIGHT }}
          >
            <Stack
              alignItems="center"
              component={Link}
              direction="row"
              display="inline-flex"
              to={paths.index}
              spacing={1}
              sx={{ textDecoration: 'none' }}
            >
              <Box
                sx={{
                  display: 'inline-flex',
                  height: 48,
                  width: 48
                }}
              >
                <Logo />
              </Box>
              <AppName sx={{ pl: 2 }}>{style.appName}</AppName>
            </Stack>
          </Stack>
        </Container>
      </Header>
      <Body>
        <Container
          maxWidth="sm"
          sx={{
            py: {
              xs: '60px',
              md: '120px'
            }
          }}
        >
          {children}
        </Container>
      </Body>
      <Box
        position="absolute"
        bottom={20}
        right={20}
      >
        <SentryButton />
      </Box>
    </Root>
  );
};
